import { ME } from '../../3-store/core/me';
import { LINK, clearPeerGrants, peers, setPeerGrant, type Peer } from '../../3-store/core/peers';
import { CLASS, queueCls, setMyGrant, setNotice } from '../../3-store/meeting/classroom';
import { isTeacherAt, speakKinds } from '../../../shared/world/classroom';
import { SCHOOL_GROUP, linkGroup } from '../../../shared/world/links';
import type { Kind, ServerOf } from '../../../shared/protocol';

// 교실 수업 (사용자가 정했다, docs 9장): 교탁 자리에 앉은 사람이 강사. 강사가 있으면 강사와 발언권을 받은 학생만 말하고 나머지는 듣기만 한다. 강사가 없으면 학교 안 전원이 말한다.
// 서버가 같은 규칙(shared/world/classroom.ts)으로 SFU 올리기·화면 공유를 막는다. 여기는 화면: 단추를 잠그고, 자격을 잃은 트랙을 끄고, 직접 연결로 온 소리를 가린다.
const linkGroupOf = (p: Pick<Peer, 'room' | 'seat'>) => linkGroup(p.room, p.seat);   // 2-controllers/meeting/links.ts의 linkGroupOf와 같은 함수(서로 가져오면 순환이 된다)
export const amTeacher = () => isTeacherAt(ME.room, ME.seat);
export const isTeacherPeer = (r: Pick<Peer, 'room' | 'seat'>) => isTeacherAt(r.room, r.seat);
export const inSchool = () => linkGroupOf(ME) === SCHOOL_GROUP;
export const teacherHere = () => inSchool() && (amTeacher() || [...peers.values()].some((r) => linkGroupOf(r) === SCHOOL_GROUP && isTeacherPeer(r)));
// 강사가 있는 교실에서 내가 학생인가
export const lecturing = () => teacherHere() && !amTeacher();

// 내가 지금 내보낼 수 있는 종류 (마이크·카메라·화면 공유)
export const myKinds = (): Kind[] => speakKinds({ room: ME.room, seat: ME.seat, teacherHere: teacherHere(), granted: CLASS.granted });
// 잠긴 단추에 붙이는 짧은 이유. 잠기지 않았으면 ''
export const lockReason = (k: Kind) => (myKinds().includes(k) ? '' : k === 'cam' ? '강사만' : '발언권 필요');

// 직접 연결로 오는 그 사람의 소리·영상을 받아들이나: 강사가 있는 교실에서는 강사와 발언권 학생의 것만
export function heard(id: string) {
  const r = peers.get(id);
  if (!r || !inSchool() || linkGroupOf(r) !== SCHOOL_GROUP || !teacherHere()) return true;
  return isTeacherPeer(r) || !!r.gr;
}

// 강사 화면의 목록: 손 든 학생(든 순서)과 발언권을 가진 학생
export type HandRow = { id: string; name: string; granted: boolean };
export function handRows(): HandRow[] {
  if (!amTeacher()) return [];
  const here = [...peers.values()].filter((r) => linkGroupOf(r) === SCHOOL_GROUP && !isTeacherPeer(r));
  return [
    ...here.filter((r) => r.hd && !r.gr).sort((a, b) => a.hdAt - b.hdAt).map((r) => ({ id: r.id, name: r.name, granted: false })),
    ...here.filter((r) => r.gr).map((r) => ({ id: r.id, name: r.name, granted: true })),
  ];
}
// 발언권을 주거나 거둔다 (강사만. 서버가 다시 검사한다)
export function toggleGrant(id: string) { const r = peers.get(id); if (amTeacher() && r) queueCls(r.gr ? 'revoke' : 'grant', id); }

export function onCls(m: ServerOf<'cls'>) {
  if (m.k === 'clear') { if (CLASS.granted) setNotice('강사가 자리를 비워 발언권이 끝났어요'); setMyGrant(false); clearPeerGrants(); return; }
  if (typeof m.id !== 'string' || (m.k !== 'grant' && m.k !== 'revoke')) return;
  setPeerGrant(m.id, m.k === 'grant');
  if (m.id === LINK.id) { setMyGrant(m.k === 'grant'); setNotice(m.k === 'grant' ? '발언권을 받았어요. 마이크와 화면 공유를 쓸 수 있어요' : '발언권이 끝났어요'); }
}
// 새로 접속했다: 서버는 이 접속에 발언권이 없다고 안다
export function resetClass() { setMyGrant(false); }
